import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuthState } from '../hooks/useAuthState';
import { isEventChatAvailable, getEventChatOpensAt } from '../lib/chat-utils';
import '../styles/EventAttendees.css';

const EventAttendees = ({ event, rsvpState, showBlur = false, canInvite = false, canChat = false, canBroadcast = false, onInviteClick, onInviteFriendClick, onQRClick, onChatClick, onBroadcastClick, onAttendeeClick }) => {
  const { user } = useAuthState();
  const [attendees, setAttendees] = useState([]);
  const [attendeesWithData, setAttendeesWithData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(false);

  useEffect(() => {
    if (event) {
      if (!user) {
        setCanView(false);
        setLoading(false);
        return;
      }
      checkVisibilityAndLoad();
    }
  }, [event, user, rsvpState]);

  const checkVisibilityAndLoad = async () => {
    if (!event || !user) return;

    // Check if user can view attendees based on rules
    const visibility = event.rules?.attendeesVisible || 'off';
    const isHost = event.hostId === user.uid;
    
    // Host can always see
    if (isHost) {
      setCanView(true);
      await loadAttendees();
      return;
    }

    // Check visibility rules
    if (visibility === 'off') {
      setCanView(false);
      setLoading(false);
      return;
    }

    if (visibility === 'friends_only') {
      // Simplified: if user has RSVP'd, they can see
      setCanView(rsvpState === 'going' || rsvpState === 'checked_in');
    } else if (visibility === 'mutuals_only') {
      // Would need to check mutual follows - for now, if RSVP'd
      setCanView(rsvpState === 'going' || rsvpState === 'checked_in');
    } else {
      // Public or other - allow viewing
      setCanView(true);
    }

    if (canView) {
      await loadAttendees();
    } else {
      setLoading(false);
    }
  };

  const loadAttendees = async () => {
    if (!event) return;

    setLoading(true);
    try {
      // Get all RSVPs with state 'going' or 'checked_in'
      const rsvpsRef = collection(db, 'events', event.id, 'rsvps');
      const rsvpsSnapshot = await getDocs(rsvpsRef);
      
      const attendeesList = [];
      rsvpsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.state === 'going' || data.state === 'checked_in') {
          attendeesList.push({
            userId: doc.id,
            state: data.state,
            plusOnes: data.plusOnes || 0,
            checkedInAt: data.checkedInAt,
          });
        }
      });

      setAttendees(attendeesList);

      // Load user data for each attendee
      const attendeesWithUserData = await Promise.all(
        attendeesList.map(async (attendee) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', attendee.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                ...attendee,
                firstName: userData.firstName,
                lastName: userData.lastName,
                profilePicture: userData.profilePicture,
              };
            }
            return attendee;
          } catch (err) {
            console.error(`Error loading user data for ${attendee.userId}:`, err);
            return attendee;
          }
        })
      );

      setAttendeesWithData(attendeesWithUserData);
    } catch (error) {
      console.error('Error loading attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="attendees-section">
        <div className="loading-small">Loading attendees...</div>
      </div>
    );
  }

  // When attendees are private: show action cards (like groups) then "Guest list is private"
  if (!canView) {
    const chatAvailable = event && isEventChatAvailable(event);
    const chatOpensAt = event && getEventChatOpensAt(event);
    const rsvpLabel = rsvpState === 'going' ? 'Going' : rsvpState === 'interested' ? 'Maybe' : rsvpState === 'cancelled' ? "Can't go" : 'You';

    return (
      <div className="attendees-section">
        <h3>Attendees</h3>
        <div className="attendees-scroll-container">
          <div className="attendees-row attendees-row-cards-only">
            {canInvite && onInviteClick && (
              <div
                className="attendee-card-square attendee-card-invite"
                onClick={onInviteClick}
                style={{ cursor: 'pointer' }}
              >
                <div className="attendee-card-image attendee-card-invite-image">
                  <div className="attendee-card-invite-icon">+</div>
                </div>
                <div className="attendee-card-name attendee-card-invite-text">Invite</div>
              </div>
            )}
            {user && (
              <div className="attendee-card-square attendee-card-you" title={rsvpState ? `You: ${rsvpLabel}` : 'Your RSVP'}>
                <div className="attendee-card-image attendee-card-you-image">
                  <div className="attendee-card-invite-icon">👤</div>
                </div>
                <div className="attendee-card-name attendee-card-invite-text">{rsvpState ? rsvpLabel : 'You'}</div>
              </div>
            )}
            {canChat && onChatClick && (
              <div
                className="attendee-card-square attendee-card-chat"
                onClick={chatAvailable ? onChatClick : undefined}
                style={{ cursor: chatAvailable ? 'pointer' : 'default' }}
                title={chatAvailable ? 'Open Event Chat' : (chatOpensAt ? `Chat opens 24 hours before event (${chatOpensAt.toLocaleString()})` : 'Chat opens 24 hours before event')}
              >
                <div className="attendee-card-image attendee-card-chat-image">
                  <div className="attendee-card-chat-icon">💬</div>
                </div>
                <div className="attendee-card-name attendee-card-chat-text">Chat</div>
                {event?.groupChat?.enabled && !chatAvailable && chatOpensAt && (
                  <div className="attendee-card-chat-hint">Opens 24h before</div>
                )}
              </div>
            )}
            {canBroadcast && onBroadcastClick && (
              <div
                className="attendee-card-square attendee-card-broadcast"
                onClick={onBroadcastClick}
                style={{ cursor: 'pointer' }}
                title="Broadcast message to all attendees"
              >
                <div className="attendee-card-image attendee-card-broadcast-image">
                  <div className="attendee-card-broadcast-icon">📢</div>
                </div>
                <div className="attendee-card-name attendee-card-broadcast-text">Broadcast</div>
              </div>
            )}
            {onQRClick && canInvite && (
              <div
                className="attendee-card-square attendee-card-invite-friend"
                onClick={onQRClick}
                style={{ cursor: 'pointer' }}
                title="Show QR code for invite"
              >
                <div className="attendee-card-image attendee-card-invite-image">
                  <div className="attendee-card-invite-icon">📱</div>
                </div>
                <div className="attendee-card-name attendee-card-invite-text">QR code</div>
              </div>
            )}
          </div>
        </div>
        <p className="attendees-private-message">Guest list is private.</p>
      </div>
    );
  }

  if (attendees.length === 0) {
    return (
      <div className="attendees-section">
        <h3>Attendees</h3>
        <div className="attendees-scroll-container">
          <div className="attendees-row">
            {canChat && onChatClick && (
              <div
                className="attendee-card-square attendee-card-chat"
                onClick={onChatClick}
                style={{ cursor: 'pointer' }}
                title="Open Event Chat"
              >
                <div className="attendee-card-image attendee-card-chat-image">
                  <div className="attendee-card-chat-icon">💬</div>
                </div>
                <div className="attendee-card-name attendee-card-chat-text">Chat</div>
              </div>
            )}
            {canBroadcast && onBroadcastClick && (
              <div
                className="attendee-card-square attendee-card-broadcast"
                onClick={onBroadcastClick}
                style={{ cursor: 'pointer' }}
                title="Broadcast message to all attendees"
              >
                <div className="attendee-card-image attendee-card-broadcast-image">
                  <div className="attendee-card-broadcast-icon">📢</div>
                </div>
                <div className="attendee-card-name attendee-card-broadcast-text">Broadcast</div>
              </div>
            )}
            {canInvite && onInviteClick && (
              <div
                className="attendee-card-square attendee-card-invite"
                onClick={onInviteClick}
                style={{ cursor: 'pointer' }}
                title="Invite from contacts or friends"
              >
                <div className="attendee-card-image attendee-card-invite-image">
                  <div className="attendee-card-invite-icon">+</div>
                </div>
                <div className="attendee-card-name attendee-card-invite-text">Invite</div>
              </div>
            )}
            {canInvite && onQRClick && (
              <div
                className="attendee-card-square attendee-card-invite-friend"
                onClick={onQRClick}
                style={{ cursor: 'pointer' }}
                title="Show QR code for invite"
              >
                <div className="attendee-card-image attendee-card-invite-image">
                  <div className="attendee-card-invite-icon">📱</div>
                </div>
                <div className="attendee-card-name attendee-card-invite-text">QR code</div>
              </div>
            )}
          </div>
        </div>
        <p className="empty-attendees">No attendees yet</p>
      </div>
    );
  }

  // Calculate total (including plus-ones)
  const totalAttendees = attendees.reduce(
    (sum, attendee) => sum + 1 + (attendee.plusOnes || 0),
    0
  );

  return (
    <div className="attendees-section">
      <h3>
        Attendees{' '}
        {event.rules?.attendeesVisible !== 'off' && (
          <span className="attendee-count">({totalAttendees})</span>
        )}
      </h3>
      <div className="attendees-scroll-container">
        <div className={`attendees-row ${showBlur ? 'attendees-blurred' : ''}`}>
          {/* Chat Button - Card style */}
          {canChat && (
            <div
              className="attendee-card-square attendee-card-chat"
              onClick={onChatClick}
              style={{ cursor: 'pointer' }}
              title="Open Event Chat"
            >
              <div className="attendee-card-image attendee-card-chat-image">
                <div className="attendee-card-chat-icon">💬</div>
              </div>
              <div className="attendee-card-name attendee-card-chat-text">Chat</div>
            </div>
          )}
          
          {/* Broadcast Button - Only for hosts/cohosts */}
          {canBroadcast && onBroadcastClick && (
            <div
              className="attendee-card-square attendee-card-broadcast"
              onClick={onBroadcastClick}
              style={{ cursor: 'pointer' }}
              title="Broadcast message to all attendees"
            >
              <div className="attendee-card-image attendee-card-broadcast-image">
                <div className="attendee-card-broadcast-icon">📢</div>
              </div>
              <div className="attendee-card-name attendee-card-broadcast-text">Broadcast</div>
            </div>
          )}
          
          {/* Invite - opens contacts/friends modal (like app) */}
          {canInvite && onInviteClick && (
            <div
              className="attendee-card-square attendee-card-invite"
              onClick={onInviteClick}
              style={{ cursor: 'pointer' }}
              title="Invite from contacts or friends"
            >
              <div className="attendee-card-image attendee-card-invite-image">
                <div className="attendee-card-invite-icon">+</div>
              </div>
              <div className="attendee-card-name attendee-card-invite-text">Invite</div>
            </div>
          )}
          {/* QR Code */}
          {canInvite && onQRClick && (
            <div
              className="attendee-card-square attendee-card-invite-friend"
              onClick={onQRClick}
              style={{ cursor: 'pointer' }}
              title="Show QR code for invite"
            >
              <div className="attendee-card-image attendee-card-invite-image">
                <div className="attendee-card-invite-icon">📱</div>
              </div>
              <div className="attendee-card-name attendee-card-invite-text">QR code</div>
            </div>
          )}
          
          {attendeesWithData.map((attendee) => {
            const displayName = attendee.firstName && attendee.lastName
              ? `${attendee.firstName} ${attendee.lastName}`.trim()
              : attendee.firstName || attendee.lastName || 'Attendee';
            const initials = displayName
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);
            
            const isCurrentUser = attendee.userId === user?.uid;

            return (
              <div
                key={attendee.userId}
                className="attendee-card-square"
                onClick={() => {
                  if (!isCurrentUser && onAttendeeClick) {
                    onAttendeeClick(attendee.userId);
                  }
                }}
                style={{ cursor: !isCurrentUser ? 'pointer' : 'default' }}
              >
                <div className="attendee-card-image">
                  {attendee.profilePicture ? (
                    <img 
                      src={attendee.profilePicture} 
                      alt={displayName}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="attendee-card-avatar">
                      {initials || '👤'}
                    </div>
                  )}
                </div>
                <div className="attendee-card-name">
                  {isCurrentUser ? 'You' : displayName}
                </div>
                {attendee.state === 'checked_in' && (
                  <div className="attendee-card-badge">✓</div>
                )}
              </div>
            );
          })}
        </div>
        {showBlur && (
          <div className="attendees-blur-overlay">
            <p>RSVP to see attendees</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventAttendees;

